import React, { useState, useContext } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { Box, Alert, CircularProgress } from '@mui/material';
import { AuthContext } from '../contexts/AuthContext';
import { SplitAuthLayout, AmberButton, AuthInput } from '../components/design';

const Register = () => {
  const [formData, setFormData] = useState({ username: '', email: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const subscriptionData = location.state || {};
  const redirectToCheckout = subscriptionData.redirectToCheckout;

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) return setError('Passwords do not match');
    if (formData.password.length < 6) return setError('Password must be at least 6 characters');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) return setError('Please enter a valid email address');

    setLoading(true);
    try {
      await register({ username: formData.username, email: formData.email, password: formData.password });
      if (redirectToCheckout) {
        navigate('/checkout', { state: subscriptionData });
      } else {
        navigate('/verify-email', { state: { email: formData.email } });
      }
    } catch (err) {
      setError(err.msg || 'Registration failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <SplitAuthLayout statusLine="// create account · unlimited drawings">
      <Box component="h1" sx={{ m: 0, mb: '6px', fontSize: '24px', fontWeight: 700, color: '#f4f8ff', letterSpacing: '-.01em' }}>
        Create your account
      </Box>
      <Box sx={{ mb: '22px', fontSize: '14.5px', color: '#8fa3c6' }}>
        Takes less than a minute.
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: '20px', background: 'rgba(244,113,106,.1)', color: '#f4a39e', border: '1px solid rgba(244,113,106,.3)', '& .MuiAlert-icon': { color: '#f4716a' } }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <AuthInput label="Username" id="username" name="username" value={formData.username} onChange={handleChange} placeholder="j.smith" autoComplete="username" autoFocus required />
          <AuthInput label="Email" id="email" type="email" name="email" value={formData.email} onChange={handleChange} placeholder="you@company.com" autoComplete="email" required />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <AuthInput label="Password" id="password" type="password" name="password" value={formData.password} onChange={handleChange} placeholder="••••••••" autoComplete="new-password" required />
          <AuthInput label="Confirm" id="confirmPassword" type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} placeholder="••••••••" required />
        </Box>
        <Box sx={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', color: '#5f7398' }}>
          min. 6 characters
        </Box>

        <AmberButton size="lg" fullWidth disabled={loading} onClick={handleSubmit}>
          {loading ? <CircularProgress size={18} sx={{ color: '#241200' }} /> : 'Create account'}
        </AmberButton>
      </Box>

      <Box sx={{ mt: '20px', textAlign: 'center', fontSize: '13.5px', color: '#8298bd' }}>
        Already have an account?{' '}
        <Box component={RouterLink} to="/login" sx={{ color: '#2dd4bf', textDecoration: 'none', fontWeight: 500, '&:hover': { color: '#5fe6d6' } }}>
          Log in
        </Box>
      </Box>
    </SplitAuthLayout>
  );
};

export default Register;
